import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { ERROR_CODES } from '@pxc/shared';
import type { AdCompletionDto, AdSessionDto, MineDto, MinerDto, MinerFilter } from '@pxc/shared';
import { useErrorCopy } from '../../lib/errorCopy.js';
import { ApiError, newIdempotencyKey } from '../../services/api.js';
import { type AdClick, closeOrRecover, completeWhenConfirmed, passHumanCheck, requiredCheck } from '../../services/adFlow.js';
import { createAdRunner } from '../../services/ads.js';
import { humanPass } from '../../services/captcha.js';
import { api, queryKeys } from '../../services/endpoints.js';
import { reloadIfStale } from '../../services/freshness.js';
import { haptics, setClosingConfirmation } from '../../services/telegram.js';
import { useAdStore } from '../../stores/ad.store.js';
import { toast } from '../../stores/toast.store.js';
import { notice } from '../../stores/notice.store.js';
import { useT } from '../../i18n/index.js';

/**
 * Mining data and actions.
 *
 * The rule that shapes all of this: a mutation's response *is* the new truth.
 * Every successful call replaces the cache with what the server returned rather
 * than patching a local copy, so the UI cannot drift from the ledger.
 */

export function useMine() {
  return useQuery({
    queryKey: queryKeys.mine,
    queryFn: api.mine.get,
    staleTime: 5_000,
    // While a shift is running the accrued figure moves, so refresh gently.
    // When nothing is working there is nothing to refresh.
    refetchInterval: (query) => {
      const data = query.state.data as MineDto | undefined;
      return data && data.summary.activeMiners > 0 ? 30_000 : false;
    },
    refetchOnWindowFocus: true,
  });
}

export function useMinerList(filter: MinerFilter) {
  return useQuery({
    queryKey: queryKeys.miners(filter),
    queryFn: () => api.miners.list(filter),
    staleTime: 5_000,
  });
}

function useRefreshMining() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.mine });
    void queryClient.invalidateQueries({ queryKey: ['miners'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.wallet });
    void queryClient.invalidateQueries({ queryKey: ['activities'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
  }, [queryClient]);
}


/**
 * The pause between two ads of one shift: 30 seconds, by the owner's call.
 * It also keeps clear of Adsgram, which refuses "several ads in a row"
 * (onNonStopShow) and asks for a few seconds; the next ad's button holds this
 * long rather than let a quick tap run into that refusal.
 */
const NEXT_AD_DELAY_MS = 30_000;


/**
 * The activation flow (§2, §10):
 *
 *   human check → open session → play ad → server verifies → server opens the shift → refresh
 *
 * When the shift takes several ads, every one goes through that same path and
 * the server answers AD_PROGRESS until the last: it was counted, nothing has
 * started, and the next one is the user's tap away.
 *
 * Note what is missing: any branch where the client decides the ad completed
 * and grants something itself — or that it passed the human check: the server
 * refuses the ad without its own record of a pass (CAPTCHA_REQUIRED), and the
 * flow answers that by showing the box again, once.
 */
export function useActivateMiner() {
  const { t } = useT();
  const describeError = useErrorCopy();
  const refresh = useRefreshMining();
  const running = useRef(false);

  const activate = useCallback(
    async (miner: MinerDto): Promise<boolean> => {
      // Read the store imperatively: subscribing here would re-render whichever
      // screen called this hook on every ad-progress tick, and that screen is
      // usually the one holding the mine.
      const store = useAdStore.getState();

      // Guards against a double tap producing two ad sessions.
      if (running.current) return false;
      running.current = true;
      store.begin(miner.userMinerId, miner.name);
      store.setCount(miner.adsWatched ?? 0, miner.adsRequired);
      setClosingConfirmation(true);

      // Whether a failure belongs to the human check rather than to an ad.
      let checking = true;
      try {
        // No ad, and so no shift, without the human check (when it is due).
        if (!(await passHumanCheck())) return false;
        checking = false;

        let session: AdSessionDto;
        try {
          session = await api.ads.createSession(miner.userMinerId);
        } catch (error) {
          // The server's record says the check is due after all (the pass ran
          // out on the way, or the panel shortened the window): ask for it and
          // try once more.
          const asked = requiredCheck(error);
          if (!asked) throw error;
          humanPass.clear();
          if (!(await passHumanCheck(asked))) return false;
          session = await api.ads.createSession(miner.userMinerId);
        }
        // The server's count is the one that holds.
        store.setProvider(session.provider);
        store.setCount(session.adsWatched, session.adsRequired);
        store.setClickRequired(session.adsgram?.clickRequired === true, session.adsgram?.clickDwellSeconds ?? 0);

        store.setPhase('playing');
        store.setProgress(0, 0);

        const runner = createAdRunner(session.provider);
        let completion: AdCompletionDto;
        const click: AdClick = { via: null, dwellMs: null };
        try {
          const { providerToken } = await runner.run(session, {
            onStarted: () => store.setPhase('playing'),
            onProgress: ({ remainingSeconds, totalSeconds }) =>
              store.setProgress(remainingSeconds, totalSeconds),
            onClicked: (via) => {
              click.via ??= via;
            },
            onClickDwell: (ms) => {
              click.dwellMs = ms;
            },
          });

          // The ad is over, but nothing has been granted yet — the backend has
          // the last word.
          store.setPhase('verifying');
          completion = await completeWhenConfirmed(session.adSessionId, session.nonce, providerToken, click);
        } catch (error) {
          // Failed, closed, none to show, or never confirmed: that session is
          // closed so it cannot be counted later (closeOrRecover).
          completion = await closeOrRecover(session, error, () => store.setPhase('verifying'), click);
        }

        if (completion.result.kind === 'AD_PROGRESS') {
          // Verified and counted, but the shift needs more: nothing has started.
          haptics.notify('success');
          store.setCount(completion.result.adsWatched, completion.result.adsRequired);
          store.setNextAdAt(Date.now() + NEXT_AD_DELAY_MS);
          store.setPhase('progress');
          refresh();
          return false;
        }

        haptics.notify('success');
        toast.success(t('toast.shiftStarted', { miner: miner.name }), t('toast.shiftStartedBody'));
        store.reset();
        refresh();
        return true;
      } catch (error) {
        const described = describeError(error);
        store.fail({
          title: described.title,
          ...(described.description ? { description: described.description } : {}),
          ...(checking || requiredCheck(error) ? { check: true } : {}),
        });
        haptics.notify('error');
        // The miner's real state may have changed underneath us.
        refresh();
        // A code this build does not know means the server is newer than the
        // app: take the new one (at most once per version), a moment after the
        // message is up, so the next try speaks the server's language.
        if (error instanceof ApiError && !(ERROR_CODES as readonly string[]).includes(error.code)) {
          window.setTimeout(() => void reloadIfStale(), 2500);
        }
        return false;
      } finally {
        running.current = false;
        setClosingConfirmation(false);
      }
    },
    [refresh],
  );

  return { activate };
}

export function useClaimShift() {
  const { t } = useT();
  const describeError = useErrorCopy();
  const refresh = useRefreshMining();

  return useMutation({
    mutationFn: ({ shiftPublicId }: { shiftPublicId: string; minerName: string }) =>
      api.mining.claim(shiftPublicId, newIdempotencyKey()),
    onSuccess: (result, variables) => {
      haptics.notify('success');
      notice.reward(
        `+${unsigned(result.transaction.amount)} Gems`,
        t('toast.shiftClaimed', { miner: variables.minerName }),
      );
      refresh();
    },
    onError: (error) => {
      const described = describeError(error);
      toast.error(described.title, described.description);
      haptics.notify('error');
      refresh();
    },
  });
}

export function useClaimAll() {
  const { t, plural } = useT();
  const describeError = useErrorCopy();
  const refresh = useRefreshMining();

  return useMutation({
    mutationFn: () => api.mining.claimAll(newIdempotencyKey()),
    onSuccess: (result) => {
      haptics.notify('success');
      toast.success(
        t('mining.claimedAllTitle', { amount: result.total }),
        plural('mining.claimedAllBody', result.claimedCount),
      );
      refresh();
    },
    onError: (error) => {
      const described = describeError(error);
      toast.error(described.title, described.description);
      refresh();
    },
  });
}

/** The server sends amounts already signed ("+5.50"); the notice adds its own +. */
function unsigned(amount: string | number): string {
  return String(amount).replace(/^[+\-\u2212]\s*/, '');
}
